import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import {
  CourseAssignmentDTO,
  UpdateContentWatchedTime,
} from '../_models/courseAssignmentDTO';
import { environment } from '../../environments/environment';
import { endpoints } from '../_config/endpoint';
import {
  LessonRequestDTO,
  ContentVideoRequestDTO,
  contentQuizRequestDTO,
} from '../_models/course';
import { CourseStore, PaginationResponse } from '@iq-central-front-end/share';
import { catchError, tap } from 'rxjs/operators';
import { DashboardFilter } from '../interfaces/dashboard.interface';

@Injectable({
  providedIn: 'root',
})
export class CourseService {
  private initialCsDto: CourseAssignmentDTO = {
    ids: [],
    courseCodes: [],
    dueDate: '',
    participantType: '',
  };
  private csDTOData: BehaviorSubject<CourseAssignmentDTO> = new BehaviorSubject(
    this.initialCsDto
  );

  private courseStore = inject(CourseStore);

  constructor(private http: HttpClient) { }

  createCourse(certificate, courseJson, picture) {
    const newPayload = new FormData();
    newPayload.append('courseJson', courseJson);
    newPayload.append('certificate', certificate);
    newPayload.append('picture', picture);
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.createCourse}`,
      newPayload
    );
  }

  getAllCategories() {
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getAllCategories}`
    );
  }

  getAllSubcategories(categoryId) {
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getAllSubCategories}/${categoryId}/subcategories`
    );
  }

  getllTopics(subCategoryId) {
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getAllTopics}/${subCategoryId}/all`
    );
  }

  createLesson(payload: LessonRequestDTO) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.createLesson}/${payload.courseCode}/create`,
      payload
    );
  }

  createLessonContentVideo(payload: ContentVideoRequestDTO) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.createLessonContentVideo}`,
      payload
    );
  }

  createLessonContentPdf(payload: ContentVideoRequestDTO) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.createLessonContentPdf}`,
      payload
    );
  }
  getOneLesson(id, code) {
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getAllLessons}/${code}/find/${id}`
    );
  }
  getCourseById(courseCode) {
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getCourseById}/${courseCode}`
    );
  }

  generateReport(courseCode, userId) {
    const params = new HttpParams().set('userId', userId);

    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.generateReport}/${courseCode}/generate-course-report`,
      { params }
    );
  }

  generateCertificate(courseCode) {
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.generateReport}/${courseCode}/generate-course-certificate`
    );
  }

  getAllLessons(code) {
    const hasExistingData = this.courseStore.courseLessons$()?.length > 0;
    if (!hasExistingData) {
      this.courseStore.setLoading(true);
    }

    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getAllLessons}/${code}/all`
    ).pipe(
      tap((response) => {
        this.courseStore.setCourseLessons(response['responseResult']);
        this.courseStore.setLoading(false);
      }),
      catchError((error) => {
        this.courseStore.setError(error.error.responseMessage);
        this.courseStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }


  getAllCourses(id) {
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getAllCourses}/${id}`
    );
  }


  getAllCompanyCourses(page: number = 1, limit: number = 500, search: string = ''): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('searchParams', search);

    const hasExistingData = this.courseStore.courses$()?.data?.length > 0;
    if (!hasExistingData) {
      this.courseStore.setLoading(true);
    }

    return this.http.get(
      `${environment.API_URL_V1}/api/company/courses/search`,
      { params }
    ).pipe(
      tap((response) => {
        // console.log(response, 'sksk');
        const paginationResponse: PaginationResponse<any> = {
          data: response['responseResult']['courses'],
          total: response['responseResult']['totalElements'],
          page: response['responseResult']['currentPage'],
          limit: response['responseResult']['size'],
        };

        this.courseStore.setCourses(paginationResponse);
        this.courseStore.setLoading(false);
      }),
      catchError((error) => {
        this.courseStore.setError(error.error.responseMessage);
        this.courseStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getAssignedCourses(userId: any, pageNumber, pageSize) {
    const params = new HttpParams()
      .set('page', pageNumber)
      .set('size', pageSize);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getAssignedCourses}/${userId}/assigned_courses`,
      { params }
    );
  }

  updateLesson(lessonId, payload: LessonRequestDTO) {
    return this.http.put(
      `${environment.API_URL_V1}/${endpoints.getAllLessons}/${payload.courseCode}/update/${lessonId}`,
      payload
    );
  }

  updateCourse(certificate, courseCode, courseJson, picture) {
    const newPayload = new FormData();
    newPayload.append('courseJson', courseJson);
    newPayload.append('certificate', certificate);
    newPayload.append('picture', picture);
    newPayload.append('courseCode', JSON.stringify(courseCode));
    return this.http.put(
      `${environment.API_URL_V1}/${endpoints.updateCourse}/${courseCode}/update`,
      newPayload
    );
  }
  updateContentVideo(lessonContentId, payload) {
    return this.http.put(
      `${environment.API_URL_V1}/${endpoints.updateContent}/${lessonContentId}/video_update`,
      payload
    );
  }
  updateContentPdf(lessonContentId, payload) {
    return this.http.put(
      `${environment.API_URL_V1}/${endpoints.updateContent}/${lessonContentId}/pdf_update`,
      payload
    );
  }

  createQuiz(payload: contentQuizRequestDTO) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.createLessonContentQuiz}`,
      payload
    );
  }

  rateCourse(courseCode, payload) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.rateCourse}/${courseCode}/rate`,
      payload
    );
  }

  createComment(courseCode, payload) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.createComment}/${courseCode}/comment`,
      payload
    );
  }

  replyComments(commentId, payload) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.replyComment}/${commentId}/reply`,
      payload
    );
  }

  getComments(courseCode, pageNumber, pageSize) {
    const params = new HttpParams()
      .set('page', pageNumber)
      .set('size', pageSize);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getComments}/${courseCode}/comments`,
      { params }
    );
  }

  toggleEnroll(courseCode) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.enroll}/${courseCode}/toggle_enroll`,
      null
    );
  }

  // unEnroll(courseCode) {
  //   return this.http.post(
  //     `${environment.API_URL_V1}/${endpoints.unEnroll}/${courseCode}/un_enroll`,
  //     null
  //   );
  // }
  deleteFileFromCloudinary(id) {
    const params = new HttpParams().set('cloudinaryId', id);
    return this.http.delete(
      `${environment.API_URL_V1}/${endpoints.deleteFileFromCloudinary}`,
      { params }
    );
  }

  deletecontent(lessonContentId) {
    return this.http.delete(
      `${environment.API_URL_V1}/${endpoints.deleteContent}/${lessonContentId}/delete`
    );
  }

  deleteLesson(courseCode, lessonId) {
    return this.http.delete(
      `${environment.API_URL_V1}/${endpoints.deleteLesson}/${courseCode}/delete/${lessonId}`
    );
  }

  getOneContent(lessonContentId) {
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getOneContent}/${lessonContentId}`
    );
  }

  getCsDTOData() {
    return this.csDTOData.asObservable();
  }
  setIdData(response: { ids?: any[]; courseCodes?: any[]; dueDate?: '' }) {
    this.csDTOData.forEach((next) => {
      next.ids = response.ids;
    });
  }

  setParticipantType(response: { participantType: string }) {
    this.csDTOData.forEach((next) => {
      next.participantType = response.participantType;
    });
  }

  setCourseData(response: { ids?: any[]; courseCodes?: any[]; dueDate?: '' }) {
    this.csDTOData.forEach((next) => {
      next.courseCodes = response.courseCodes;
    });
  }
  setDateData(response: { ids?: any[]; courseCodes?: any[]; dueDate?: '' }) {
    this.csDTOData.forEach((next) => {
      next.dueDate = response.dueDate;
    });
  }
  reset() {
    this.csDTOData.next(this.initialCsDto);
  }

  assignCourseToStaffs(payload: CourseAssignmentDTO) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.assignCourseToIndividual}`,
      payload
    );
  }
  assignCourseToDepartments(payload: CourseAssignmentDTO) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.assignCourseToDepartment}`,
      payload
    );
  }
  assignCourseToDivisions(payload: CourseAssignmentDTO) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.assignCourseToDivision}`,
      payload
    );
  }
  assignCourseToUnits(payload: CourseAssignmentDTO) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.assignCourseToUnit}`,
      payload
    );
  }
  courseProgress(payload) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.courseProgressUpdate}`,
      payload
    );
  }

  updateLessonContentWatchedTime(payload: UpdateContentWatchedTime) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.updateLessonContentWatchedTime}`,
      payload
    );
  }

  toggleSavedCourse(courseCode, payload) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.toggleSaveCourse}/${courseCode}/toggle_save`,
      payload
    );
  }

  savedCourses(
    payload: {
      pageNumber, pageSize, searchQuery
    }
  ) {
    const params = new HttpParams()
      .set('page', payload.pageNumber)
      .set('size', payload.pageSize)
      .set('searchParams', payload.searchQuery);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.savedCourses}`,
      { params }
    );
  }

  getAssessmentResultHistory(id, pageNumber, pageSize) {
    const params = new HttpParams()
      .set('page', pageNumber)
      .set('size', pageSize);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.assessmentResultHistory
      }/${id}/${'result_history'}`,
      { params }
    );
  }

  enrolledCourses(pageNumber, pageSize) {
    const params = new HttpParams()
      .set('page', pageNumber)
      .set('size', pageSize);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getEnrolledCourses}`,
      { params }
    );
  }

  markAssessment(id, payload) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.markAssessment}/${id}/mark`,
      payload
    );
  }

  toggleLikeCourse(courseCode, payload) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.toggleLikeCourse}/${courseCode}/toggle_like`,
      payload
    );
  }

  likedCourses(
    payload: {
      pageNumber, pageSize, searchQuery
    }
  ) {
    const params = new HttpParams()
      .set('page', payload.pageNumber)
      .set('size', payload.pageSize)
      .set('searchParams', payload.searchQuery);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.likedCourses}`,
      { params }
    );
  }

  deleteCourse(courseCode) {
    return this.http.delete(
      `${environment.API_URL_V1}/${endpoints.deleteCourse}/${courseCode}/delete`
    );
  }

  unassignCourse(payload: { courseCode: string; id: number }) {
    return this.http.post(
      `${environment.API_URL_V1}/${endpoints.unassign}`,
      payload
    );
  }

  getEnterpriseDueCourses(payload: DashboardFilter) {
    const hasExistingData = this.courseStore.dueCourses$()?.length > 0;
    if (!hasExistingData) {
      this.courseStore.setLoading(true);
    }

    return this.http.post(
      `${environment.API_URL_V1}/api/enterprise/due-courses`,
      payload
    ).pipe(
      tap((response) => {
        this.courseStore.setDueCourses(response['responseResult']);
        this.courseStore.setLoading(false);
      }),
      catchError((error) => {
        this.courseStore.setError(error.error.responseMessage);
        this.courseStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  getEnrolledCourses(userId: any, pageNumber, pageSize) {
    const hasExistingData = this.courseStore.enrolledCourses$()?.data?.length > 0;
    if (!hasExistingData) {
      this.courseStore.setLoading(true);
    }

    const params = new HttpParams()
      .set('page', pageNumber)
      .set('size', pageSize);
    return this.http.get(
      `${environment.API_URL_V1}/${endpoints.getEnterpriseEnrolledCourses}/${userId}/enrolled_courses`,
      { params }
    ).pipe(
      tap((response) => {
        const paginationResponse: PaginationResponse<any> = {
          data: response['responseResult']['enrolledCourses'],
          total: response['responseResult']['paginationInfo']['totalElements'],
          page: response['responseResult']['paginationInfo']['number'],
          limit: response['responseResult']['paginationInfo']['size'],
        }
        this.courseStore.setEnrolledCourses(paginationResponse);
        this.courseStore.setLoading(false);
      }),
      catchError((error) => {
        this.courseStore.setError(error.error.responseMessage);
        this.courseStore.setLoading(false);
        return throwError(() => error);
      })
    );
  }
}
